import React, { useEffect, useState, useCallback } from "react";
import { secureGet } from "../../config/api";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import PageScroller from "../../components/PageScroller";
import Filter from "../../components/Filter";
import LoadingLogo from "../../components/LoadingLogo";
import SnackbarNotification from "../../components/SnackbarNotification";
import Table from "../../components/Table";

const Products = () => {
  const [products, setProducts] = useState([]);
  const [repos, setRepos] = useState([]);
  const [loading, setLoading] = useState(true);

  const [pageNumber, setPageNumber] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(30);

  const [filters, setFilters] = useState({ needle: "", repoId: "" });

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const { t } = useTranslation();

  const showSnackbar = (message, severity = "success", manual = false) =>
    setSnackbar({ open: true, message, severity, manual });

  const filterOptions = [
    { name: "needle", type: "search" },
    {
      name: "repoId",
      type: "dropdown",
      label: t("fields.inventory.repo"),
      options: repos.map((r) => ({ value: String(r.id), label: r.name })),
    },
  ];

  const fetchProducts = useCallback(
    async (custom = filters, page = pageNumber, size = pageSize) => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (custom.needle) params.append("needle", custom.needle);
        if (custom.repoId) params.append("repoId", custom.repoId);
        params.append("page", String(page));
        params.append("size", String(size));

        const url = `/inventories/products?${params.toString()}`;
        const res = await secureGet(url, showSnackbar);
        const data = res?.data || {};

        setProducts(data.content || []);
        setTotalPages(data.totalPages ?? 1);
        setPageNumber(data.number ?? page);
        setPageSize(data.size ?? size);
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    },
    [filters, pageNumber, pageSize]
  );

  useEffect(() => {
    (async () => {
      try {
        const repoRes = await secureGet("/repos?size=1000", showSnackbar);
        setRepos(repoRes?.data?.content || []);
        const reset = { needle: "", repoId: repoRes.data.content[0].id };
        setFilters(reset);
        fetchProducts({ needle: "", repoId: repoRes.data.content[0].id }, 0, pageSize);
      } catch (e) {
        console.error("Error fetching repositories:", e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageChange = (p) => {
    if (p >= 0 && p < totalPages) {
      setPageNumber(p);
      fetchProducts(filters, p, pageSize);
    }
  };

  const handlePageSizeChange = (n) => {
    setPageSize(n);
    setPageNumber(0);
    fetchProducts(filters, 0, n);
  };

  const handleFilterChange = (name, value) => {
    const updated = { ...filters, [name]: value };
    setFilters(updated);
    setPageNumber(0);
    fetchProducts(updated, 0, pageSize);
  };

  const resetFilters = () => {
    const reset = { needle: "", repoId: repos[0].id };
    setFilters(reset);
    setPageNumber(0);
    fetchProducts(reset, 0, pageSize);
  };

  const columns = [
    { key: "reference", label: "Référence" },
    { key: "designation", label: "Désignation" },
    { key: "quantity", label: "Quantité" },
  ];

  const tableData = products.map((p) => ({
    reference: String(p.productReference || "").toUpperCase(),
    designation: p.productName || "",
    quantity: p.quantity ?? 0,
  }));

  return (
    <div>
      <Helmet>
        <title>GrayFox - {t("titles.products")}</title>
      </Helmet>

      <div className="flex items-center mb-[15px] justify-between">
        <h1 className="text-[40px] font-semibold">{t("titles.products")}</h1>
      </div>

      <Filter
        filters={filterOptions}
        onFilterChange={handleFilterChange}
        resetFilters={resetFilters}
        filterValues={filters}
      />

      {loading ? (
        <div className="mt-6">
          <LoadingLogo />
        </div>
      ) : (
        <div className="">
          <Table data={tableData} columns={columns} />
        </div>
      )}

      <PageScroller
        pageNumber={pageNumber}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
      />

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default Products;
